// ── Enums ──────────────────────────────────────────────────────────────────────

export type OpenItemStatus =
  | 'Open'
  | 'PartiallyAllocated'
  | 'FullyAllocated'
  | 'Overpaid'
  | 'Reversed'
  | 'Cancelled'
  | 'WrittenOff';

export type OpenItemType =
  | 'ArInvoice'
  | 'ArDebitNote'
  | 'ArCreditNote'
  | 'CustomerReceipt'
  | 'ApBill'
  | 'ApDebitNote'
  | 'ApCreditNote'
  | 'SupplierPayment'
  | 'OpeningBalance'
  | 'AdvanceReceipt'
  | 'AdvancePayment';

/** Mirrors FinOpenItem.IsCreditItem: these reduce the party's balance and display as minus. */
export const isCreditItem = (i: { documentType: string } | undefined) =>
  !!i && ['SupplierPayment', 'CustomerReceipt', 'ApCreditNote', 'ArCreditNote',
          'AdvancePayment', 'AdvanceReceipt'].includes(i.documentType);

export type PartyType = 'Customer' | 'Supplier';

export type AgeingBucket = 'Current' | '1-30' | '31-60' | '61-90' | '91-120' | '120+';

// ── DTOs ──────────────────────────────────────────────────────────────────────

export interface OpenItemDto {
  id:                   string;
  companyId:            string;
  financialYearCode:    string;
  financialPeriodCode:  string;
  locationId?:          string | null;
  costCenterId?:        string | null;
  partyType:            string;
  partyId:              string;
  partyCode:            string;
  partyName:            string;
  documentType:         string;
  documentNumber:       string;
  documentDate:         string;
  dueDate?:             string | null;
  currencyCode:         string;
  exchangeRate:         number;
  originalAmount:       number;
  allocatedAmount:      number;
  outstandingAmount:    number;
  baseOriginalAmount:   number;
  baseAllocatedAmount:  number;
  baseOutstandingAmount: number;
  status:               OpenItemStatus;
  narration?:           string | null;
  reference?:           string | null;
  sourceDocumentId?:    string | null;
  sourceModule?:        string | null;
  createdDate:          string;
  createdBy?:           string | null;
  daysOverdue?:         number | null;
  ageingBucket:         AgeingBucket;

  // ── References (P1.5) ──────────────────────────────────────────────────────
  // Three distinct identities. documentNumber above is myLedger's own (BILL-…/INV-…);
  // these are the counterparty's and the source system's.
  /** 'Local' (keyed here) or 'Integrated' (created from a source-system event). */
  origin?:                 string | null;
  /** The COUNTERPARTY's own number — the supplier's printed invoice no, or the customer's PO.
   *  The number on the statement being reconciled, so it leads in the UI. */
  externalDocumentNumber?: string | null;
  /** The originating system's document number (e.g. myPOS SINV-…). */
  sourceDocumentNumber?:   string | null;
  /** 1:N references — GRN, PO, SFA order. An invoice matches many. */
  references?:             DocumentReferenceDto[] | null;
}

/** One additional source reference on an open item. */
export interface DocumentReferenceDto {
  refType:   string;
  refNumber: string;
  refDate?:  string | null;
}

// ── Filter ────────────────────────────────────────────────────────────────────

export interface OpenItemFilterDto {
  companyId?:          string;
  locationId?:         string;
  costCenterId?:       string;
  financialYearCode?:  string;
  partyType?:          string;
  partyId?:            string;
  documentType?:       string;
  documentNumber?:     string;
  status?:             OpenItemStatus;
  currencyCode?:       string;
  dateFrom?:           string;
  dateTo?:             string;
  overdueOnly?:        boolean;
  pageNumber?:         number;
  pageSize?:           number;
  sortBy?:             string;
  sortDescending?:     boolean;
}

// ── Ageing ────────────────────────────────────────────────────────────────────

/** One column of the ageing report, from the resolved profile (GAP-011). */
export interface AgeingBucketDto {
  sequence: number;
  label:    string;
  fromDay:  number;
  toDay:    number | null;
}

export interface AgeingRowDto {
  partyId:          string;
  partyCode:        string;
  partyName:        string;
  currencyCode:     string;
  /** Aligns by index with the report's `buckets`. */
  bucketAmounts:    number[];
  totalOutstanding: number;
  /** The open items behind the totals — only when the filter sets `includeLines`. */
  lines?:           AgeingLineDto[] | null;
}

/** One open item on the ageing detail report, aged exactly as its party row. */
export interface AgeingLineDto {
  openItemId:             string;
  documentType:           string;
  documentNumber:         string;
  externalDocumentNumber: string | null;
  documentDate:           string;
  dueDate:                string | null;
  /** Days past the reference date at the as-at date; negative = not yet due. */
  days:                   number;
  /** Index into the report's `buckets`. */
  bucketIndex:            number;
  originalAmount:         number;
  outstandingAmount:      number;
}

/** The ageing report: the band definitions plus a row per party. */
export interface AgeingReportDto {
  profileId:   string | null;
  profileName: string;
  buckets:     AgeingBucketDto[];
  rows:        AgeingRowDto[];
}

export interface AgeingFilterDto {
  companyId?:    string;
  locationId?:   string;
  costCenterId?: string;
  partyType:     string;
  asAtDate:      string;
  useDueDate:    boolean;
  currencyCode?: string;
  partyId?:      string;
  profileId?:    string;
  includeLines?: boolean;
}

// ── Outstanding Summary ───────────────────────────────────────────────────────

export interface OutstandingInquiryDto {
  partyType:         string;
  partyId:           string;
  partyCode:         string;
  partyName:         string;
  financialYearCode: string;
  currencyCode:      string;
  totalOriginal:     number;
  totalAllocated:    number;
  totalOutstanding:  number;
  openItemCount:     number;
}
